import type { ComponentProps, MouseEvent } from 'react';
import { cn } from '@/lib/utils';

/** What inside a row keeps its own behaviour: a click that starts on one of these never opens the row. */
const INNER_INTERACTIVE =
  'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="menuitem"],[role="checkbox"],[role="switch"]';

/**
 * A table row that opens a target on a click anywhere in the row (decision 182, U5).
 *
 * Accessibility (WCAG 2.2 AA, U16): the `<tr>` stays a table row, so screen readers keep the
 * table's rows and cells. The row click is a **mouse convenience only**: keyboard and screen-reader
 * users open the target through a real link (`<a>` or the router's `Link`) that the caller renders
 * in the row's primary cell, pointing at the same target. Giving the row `role="link"` and a tab
 * stop instead would drop its row semantics, name the link with the whole row's text and nest the
 * row's own buttons inside a link.
 *
 * It ignores a click that starts on an inner link, button, input or label (they keep their own
 * action) and a click that ends a text selection (the person was copying, not opening).
 */
export function ClickableRow({
  onOpen,
  className,
  onClick,
  ...rest
}: ComponentProps<'tr'> & {
  /** Opens the row's target: navigate, open a sheet. */
  readonly onOpen: () => void;
}) {
  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    const target = event.target as Element;
    const inner = target.closest(INNER_INTERACTIVE);
    if (inner !== null && inner !== event.currentTarget) return;
    if ((window.getSelection()?.toString() ?? '') !== '') return;
    onOpen();
  };

  return (
    <tr
      {...rest}
      onClick={handleClick}
      className={cn('hover:bg-muted/50 cursor-pointer focus-within:bg-muted/50', className)}
    />
  );
}
